import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function Terms() {
  usePageMeta(
    "Terms of Service — Hope Chest",
    "The terms under which you use Hope Chest, the family-caregiver digital binder by Organized Hope LLC."
  );
  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935]">
      <nav className="max-w-4xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="terms-home-link">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
        <Link to="/privacy" className="text-sm text-[#7A736E] hover:text-[#3D3935]">Privacy Policy →</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Terms of Service</p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-6">The rules we share.</h1>
        <p className="text-[#7A736E] mb-2">Effective date: June 11, 2026</p>
        <p className="text-[#7A736E] mb-10">Last updated: June 11, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed">
          <section>
            <p>Welcome to Hope Chest Digital Binder ("Hope Chest"). These Terms of Service ("Terms") are the agreement between you and Organized Hope ("we", "our", "us") that governs your use of the Hope Chest service. By creating an account or using Hope Chest, you agree to these Terms.</p>
            <p className="mt-3">We've written them plainly. If anything is unclear, email us at <a href="mailto:support@organizedhope.online" className="text-[#3A5A4A] underline">support@organizedhope.online</a>.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">1. What Hope Chest is — and what it isn't</h2>
            <p>Hope Chest is a software tool that helps individual caregivers and families organize information about a loved one's care — medications, appointments, contacts, daily notes, documents, and a calendar. We call each profile a "Hope Chest."</p>
            <p className="mt-3 font-medium text-[#C87B67]">Hope Chest is not a medical device, medical advice, or a substitute for the judgment of a qualified healthcare professional.</p>
            <p className="mt-3">Decisions about a loved one's care, medications, treatments, or wellbeing must be made in consultation with qualified healthcare professionals — not based on information stored or displayed in Hope Chest. We do not diagnose, treat, or prescribe.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">2. Your account</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must be at least 18 years old to create a Hope Chest account.</li>
              <li>You are responsible for the security of your account, including your password (if you use one) and your email inbox (since we use sign-in links).</li>
              <li>You are responsible for the accuracy of the information you enter — especially medications, allergies, and emergency contacts.</li>
              <li>One account per person. Don't share accounts; instead, invite others to your Hope Chest using the built-in invite feature.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">3. Free and Premium plans</h2>
            <p>Hope Chest offers a Free plan (one Hope Chest, basic features) and a Premium plan ("Hope Chest+", unlimited Hope Chests, family collaboration, advanced reports, and other premium features).</p>
            <p className="mt-3">Premium pricing is shown on our Pricing page. We may change pricing for new subscribers at any time; existing subscribers will keep their current price for the current billing cycle and will be notified before any change at renewal.</p>
            <p className="mt-3">Premium subscriptions are billed in advance through Stripe. You can cancel anytime through Settings → Billing; cancellation takes effect at the end of your current billing period.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">4. Your content</h2>
            <p>Everything you enter into a Hope Chest — medical info, photos, documents, notes — is your content. We do not own it. You give us a limited license to store and display it as needed to provide the service to you and people you've invited.</p>
            <p className="mt-3">You agree not to upload:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Information about another person without their consent (or, for minors and dependents, their guardian's consent).</li>
              <li>Material that is illegal, violates intellectual property rights, or contains malware.</li>
              <li>Content that has nothing to do with caregiving.</li>
            </ul>
            <p className="mt-3">You can delete your content (or your entire account) anytime — see our Privacy Policy.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">5. Permission roles you grant to others</h2>
            <p>When you invite someone to a Hope Chest, you choose their role: Contributor (can edit), Viewer (read-only), or Professional (custom). You are responsible for who you invite and what permissions you give them.</p>
            <p className="mt-3">If you remove a member, they immediately lose access. Anything they added before removal remains in the Hope Chest (since it was added on your behalf).</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">6. Acceptable use</h2>
            <p>Don't use Hope Chest to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Harm, harass, or impersonate anyone.</li>
              <li>Attempt to break, hack, scrape, or overload the service.</li>
              <li>Resell access without our written permission.</li>
              <li>Send unsolicited messages through the invite feature.</li>
            </ul>
            <p className="mt-3">We reserve the right to suspend or terminate accounts that violate these terms.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">7. Service availability</h2>
            <p>We work hard to keep Hope Chest available, but we don't guarantee 100% uptime. We may need to perform maintenance, fix bugs, or improve the product, which may temporarily interrupt service.</p>
            <p className="mt-3">We may also make changes to features. We'll announce significant changes in advance when possible.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">8. Disclaimers</h2>
            <p>Hope Chest is provided "as is" and "as available." To the maximum extent permitted by law, we disclaim all warranties — including any warranty of fitness for a particular purpose, accuracy of information, or non-infringement.</p>
            <p className="mt-3 font-medium">In particular, you acknowledge that:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Hope Chest is an organization tool, not a medical service.</li>
              <li>You should not rely on Hope Chest as the sole source of any medical information.</li>
              <li>Reminders, digests, and nudges are best-effort and may not always arrive on time.</li>
              <li>Always verify medications, dosages, and appointments with your pharmacy and healthcare providers.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">9. Limitation of liability</h2>
            <p>To the maximum extent permitted by law, Organized Hope is not liable for indirect, incidental, special, consequential, or punitive damages — or for any loss of profits, data, or goodwill — arising from your use of Hope Chest.</p>
            <p className="mt-3">Our total liability for any claim relating to Hope Chest is limited to the amount you paid us in the 12 months before the claim arose, or $100, whichever is greater.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">10. Indemnification</h2>
            <p>You agree to defend and hold Organized Hope harmless from claims arising out of your misuse of Hope Chest, your content, or your violation of these Terms.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">11. Termination</h2>
            <p>You can stop using Hope Chest at any time. We can suspend or terminate your account if you violate these Terms. Provisions that by their nature should survive (sections 4, 8, 9, 10, 13) will survive termination.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">12. Governing law and disputes</h2>
            <p>These Terms are governed by the laws of the State of Alabama, USA, without regard to its conflict-of-laws principles. Any dispute will be resolved in the state or federal courts located in Madison County, Alabama, and you consent to that jurisdiction.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">13. Changes to these Terms</h2>
            <p>We may update these Terms occasionally. If we make material changes, we'll email you. Continued use after the change means you accept the updated Terms.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">14. Contact us</h2>
            <p>Organized Hope<br/>2905 Triana Boulevard<br/>Huntsville, AL 35805<br/>(256) 537-8872<br/><a href="mailto:support@organizedhope.online" className="text-[#3A5A4A] underline">support@organizedhope.online</a></p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-[#EBE5DF] text-sm text-[#7A736E] flex justify-between">
          <Link to="/" className="hover:text-[#3D3935]">← Back home</Link>
          <Link to="/privacy" className="hover:text-[#3D3935]">Privacy Policy →</Link>
        </div>
      </main>
    </div>
  );
}
